import type { Assessment, Issue, Plan, Referral, Resource, Session } from "../../services/counselingService";
import { byText, reversed, type RegisterConfig } from "../register/registerLogic";

/**
 * Counselling: sessions, cases, support plans, assessments and referrals,
 * each confidential to the counsellor who holds it. The next steps here mirror
 * the server's actions (start, complete, cancel, no-show, resolve, close...).
 */

export const day = (value?: string | null) => (value ? value.slice(0, 10) : "");
export const time = (value?: string | null) => (value ? value.slice(0, 5) : "");

type Pairs = [string, string][];
export const SESSION_TYPES: Pairs = [["individual", "Individual"], ["group", "Group"], ["family", "Family"], ["crisis", "Crisis"], ["follow_up", "Follow-up"]];
export const ISSUE_TYPES: Pairs = [["emotional", "Emotional"], ["academic", "Academic"], ["behavioral", "Behaviour"], ["social", "Social"], ["family", "Family"], ["career", "Career"], ["crisis", "Crisis"], ["other", "Other"]];
export const SEVERITIES: Pairs = [["low", "Low"], ["medium", "Medium"], ["high", "High"], ["critical", "Critical"]];
export const ASSESSMENT_TYPES: Pairs = [["initial", "Initial"], ["progress", "Progress"], ["final", "Final"], ["screening", "Screening"], ["diagnostic", "Diagnostic"]];
export const REFERRAL_TYPES: Pairs = [["external", "External"], ["internal", "Internal"], ["emergency", "Emergency"]];
export const URGENCIES: Pairs = [["low", "Low"], ["medium", "Medium"], ["high", "High"], ["urgent", "Urgent"]];
export const RESOURCE_TYPES: Pairs = [["article", "Article"], ["book", "Book"], ["video", "Video"], ["worksheet", "Worksheet"], ["assessment", "Assessment tool"], ["website", "Website"], ["other", "Other"]];
export const AUDIENCES: Pairs = [["students", "Learners"], ["parents", "Parents"], ["teachers", "Teachers"], ["counselors", "Counsellors"], ["all", "Everyone"]];
export const labelOf = (pairs: Pairs, value?: string | null) => pairs.find(([key]) => key === value)?.[1] ?? (value || "—");

type Tone = { label: string; dot: string };
const tone = (label: string, dot: string): Tone => ({ label, dot });

export function sessionStatus(row: Pick<Session, "status" | "scheduled_date">, today: string): Tone {
  if (row.status === "scheduled") return row.scheduled_date < today ? tone("Overdue", "bg-red-500") : row.scheduled_date === today ? tone("Today", "bg-amber-500") : tone("Booked", "bg-sky-500");
  return ({ in_progress: tone("In progress", "bg-amber-500"), completed: tone("Completed", "bg-emerald-500"), cancelled: tone("Cancelled", "bg-gray-400"), no_show: tone("Did not come", "bg-red-400") } as Record<string, Tone>)[row.status];
}
export const ISSUE_STATUS: Record<Issue["status"], Tone> = { reported: tone("Not assigned", "bg-red-500"), assigned: tone("Assigned", "bg-amber-500"), in_progress: tone("Open", "bg-sky-500"), resolved: tone("Resolved", "bg-emerald-500"), closed: tone("Closed", "bg-gray-400") };
export const PLAN_STATUS: Record<Plan["status"], Tone> = { active: tone("Active", "bg-emerald-500"), suspended: tone("Paused", "bg-amber-500"), completed: tone("Completed", "bg-sky-500"), cancelled: tone("Cancelled", "bg-gray-400") };
export const REFERRAL_STATUS: Record<Referral["status"], Tone> = { pending: tone("Waiting", "bg-amber-500"), accepted: tone("Accepted", "bg-sky-500"), completed: tone("Completed", "bg-emerald-500"), declined: tone("Declined", "bg-gray-400") };
export const ASSESSMENT_STATUS: Record<Assessment["status"], Tone> = { scheduled: tone("Booked", "bg-sky-500"), in_progress: tone("In progress", "bg-amber-500"), completed: tone("Completed", "bg-emerald-500"), cancelled: tone("Cancelled", "bg-gray-400") };
export const SEVERITY_TONE: Record<Issue["severity"], string> = { low: "text-gray-600", medium: "text-gray-900", high: "font-semibold text-amber-800", critical: "font-semibold text-red-700" };

/** What can be done with a session now, as the server allows it. */
export function sessionActions(row: Pick<Session, "status" | "scheduled_date">, today: string) {
  const due = row.status === "scheduled" && row.scheduled_date <= today;
  return { start: due, noShow: due, complete: row.status === "in_progress", cancel: row.status === "scheduled" || row.status === "in_progress" };
}

/** A completed session whose follow-up date has come and no later session is booked. */
export function followUpDue(row: Session, all: Session[], today: string) {
  if (row.status !== "completed" || !row.follow_up_required || !row.follow_up_date || row.follow_up_date > today) return false;
  return !all.some((other) => other.student === row.student && other.scheduled_date > row.scheduled_date && other.status !== "cancelled");
}

// ---- A learner's counselling file ---------------------------------------------

export interface LearnerFile {
  student: number;
  student_name: string;
  student_number?: string;
  class_name?: string | null;
  openCases: number;
  worstSeverity: Issue["severity"] | "";
  sessions: number;
  nextSession: string;
  lastSeen: string;
  activePlan: string;
  pendingReferrals: number;
}

const rank: Record<string, number> = { "": 0, low: 1, medium: 2, high: 3, critical: 4 };

/** Everyone the counsellor is working with, one row a learner, from the records they hold. */
export function learnerFiles(records: { sessions: Session[]; issues: Issue[]; plans: Plan[]; referrals: Referral[]; assessments: Assessment[] }, today: string): LearnerFile[] {
  const files = new Map<number, LearnerFile>();
  const touch = (row: { student: number; student_name: string; student_number?: string; class_name?: string | null }) => {
    if (!files.has(row.student)) files.set(row.student, { student: row.student, student_name: row.student_name, student_number: row.student_number, class_name: row.class_name, openCases: 0, worstSeverity: "", sessions: 0, nextSession: "", lastSeen: "", activePlan: "", pendingReferrals: 0 });
    return files.get(row.student)!;
  };
  for (const row of records.sessions) {
    const file = touch(row);
    file.sessions += 1;
    if (row.status === "scheduled" && row.scheduled_date >= today && (!file.nextSession || row.scheduled_date < file.nextSession)) file.nextSession = row.scheduled_date;
    if (row.status === "completed" && row.scheduled_date > file.lastSeen) file.lastSeen = row.scheduled_date;
  }
  for (const row of records.issues) {
    const file = touch(row);
    if (row.status !== "resolved" && row.status !== "closed") {
      file.openCases += 1;
      if (rank[row.severity] > rank[file.worstSeverity]) file.worstSeverity = row.severity;
    }
  }
  for (const row of records.plans) if (row.status === "active") touch(row).activePlan = row.title;
  for (const row of records.referrals) if (row.status === "pending" || row.status === "accepted") touch(row).pendingReferrals += 1;
  for (const row of records.assessments) touch(row);
  return [...files.values()];
}

// ---- Registers ---------------------------------------------------------------

const learnerSearch = (row: { student_name: string; student_number?: string; class_name?: string | null }) => `${row.student_name} ${row.student_number ?? ""} ${row.class_name ?? ""}`;

export function sessionConfig(today: string, all: Session[]): RegisterConfig<Session> {
  return {
    search: (row) => `${learnerSearch(row)} ${row.title} ${row.counselor_name}`,
    views: [
      { key: "upcoming", label: "Coming up", match: (row) => row.status === "scheduled" && row.scheduled_date >= today },
      { key: "today", label: "Today", match: (row) => row.scheduled_date === today && (row.status === "scheduled" || row.status === "in_progress") },
      { key: "overdue", label: "Overdue", match: (row) => row.status === "scheduled" && row.scheduled_date < today },
      { key: "follow_up", label: "Follow-up due", match: (row) => followUpDue(row, all, today) },
      { key: "done", label: "Completed", match: (row) => row.status === "completed" },
      { key: "all", label: "All" },
    ],
    date: { label: "Date", value: (row) => row.scheduled_date },
    category: { label: "Kind", value: (row) => labelOf(SESSION_TYPES, row.session_type) },
    sorts: [
      { key: "soonest", label: "Soonest first", compare: byText((row) => `${row.scheduled_date}${row.scheduled_time}`) },
      { key: "latest", label: "Latest first", compare: reversed(byText((row) => `${row.scheduled_date}${row.scheduled_time}`)) },
      { key: "name", label: "Learner A–Z", compare: byText((row) => row.student_name) },
    ],
  };
}

export const issueConfig: RegisterConfig<Issue> = {
  search: (row) => `${learnerSearch(row)} ${row.title} ${row.assigned_counselor_name ?? ""}`,
  views: [
    { key: "open", label: "Open", match: (row) => row.status !== "resolved" && row.status !== "closed" },
    { key: "urgent", label: "High or critical", match: (row) => (row.severity === "high" || row.severity === "critical") && row.status !== "resolved" && row.status !== "closed" },
    { key: "unassigned", label: "Not assigned", match: (row) => row.status === "reported" },
    { key: "resolved", label: "Resolved", match: (row) => row.status === "resolved" },
    { key: "all", label: "All" },
  ],
  date: { label: "Reported", value: (row) => row.reported_date },
  category: { label: "Kind", value: (row) => labelOf(ISSUE_TYPES, row.issue_type) },
  sorts: [
    { key: "severity", label: "Most serious first", compare: (a, b) => rank[b.severity] - rank[a.severity] || a.reported_date.localeCompare(b.reported_date) },
    { key: "newest", label: "Newest first", compare: reversed(byText((row) => row.reported_date)) },
    { key: "name", label: "Learner A–Z", compare: byText((row) => row.student_name) },
  ],
};

export function planConfig(today: string): RegisterConfig<Plan> {
  return {
    search: (row) => `${learnerSearch(row)} ${row.title}`,
    views: [
      { key: "active", label: "Active", match: (row) => row.status === "active" },
      { key: "review", label: "Review due", match: (row) => row.status === "active" && Boolean(row.review_date) && row.review_date! <= today },
      { key: "paused", label: "Paused", match: (row) => row.status === "suspended" },
      { key: "ended", label: "Completed or cancelled", match: (row) => row.status === "completed" || row.status === "cancelled" },
      { key: "all", label: "All" },
    ],
    date: { label: "Started", value: (row) => row.start_date },
    sorts: [
      { key: "review", label: "Review soonest", compare: byText((row) => row.review_date || "9999") },
      { key: "newest", label: "Newest first", compare: reversed(byText((row) => row.start_date)) },
    ],
  };
}

export const assessmentConfig: RegisterConfig<Assessment> = {
  search: (row) => `${learnerSearch(row)} ${row.title}`,
  views: [
    { key: "open", label: "Booked or in progress", match: (row) => row.status === "scheduled" || row.status === "in_progress" },
    { key: "done", label: "Completed", match: (row) => row.status === "completed" },
    { key: "all", label: "All" },
  ],
  date: { label: "Date", value: (row) => row.assessment_date },
  category: { label: "Kind", value: (row) => labelOf(ASSESSMENT_TYPES, row.assessment_type) },
  sorts: [
    { key: "soonest", label: "Soonest first", compare: byText((row) => row.assessment_date) },
    { key: "latest", label: "Latest first", compare: reversed(byText((row) => row.assessment_date)) },
  ],
};

export const referralConfig: RegisterConfig<Referral> = {
  search: (row) => `${learnerSearch(row)} ${row.referred_to ?? ""} ${row.reason}`,
  views: [
    { key: "open", label: "Waiting or accepted", match: (row) => row.status === "pending" || row.status === "accepted" },
    { key: "done", label: "Completed", match: (row) => row.status === "completed" },
    { key: "declined", label: "Declined", match: (row) => row.status === "declined" },
    { key: "all", label: "All" },
  ],
  date: { label: "Referred", value: (row) => row.referral_date },
  category: { label: "Urgency", value: (row) => labelOf(URGENCIES, row.urgency) },
  sorts: [
    { key: "urgent", label: "Most urgent first", compare: (a, b) => ["urgent", "high", "medium", "low"].indexOf(a.urgency) - ["urgent", "high", "medium", "low"].indexOf(b.urgency) },
    { key: "newest", label: "Newest first", compare: reversed(byText((row) => row.referral_date)) },
  ],
};

export const resourceConfig: RegisterConfig<Resource> = {
  search: (row) => `${row.name} ${row.description ?? ""} ${row.author ?? ""} ${row.tags.join(" ")}`,
  views: [
    { key: "all", label: "All" },
    { key: "shared", label: "Shared", match: (row) => row.is_active && row.is_public },
    { key: "private", label: "Counsellors only", match: (row) => row.is_active && !row.is_public },
    { key: "retired", label: "Retired", match: (row) => !row.is_active },
  ],
  category: { label: "For", value: (row) => labelOf(AUDIENCES, row.target_audience) },
  sorts: [{ key: "name", label: "Name A–Z", compare: byText((row) => row.name) }],
};

export const learnerFileConfig: RegisterConfig<LearnerFile> = {
  search: (row) => learnerSearch(row),
  views: [
    { key: "all", label: "All" },
    { key: "open", label: "With an open case", match: (row) => row.openCases > 0 },
    { key: "serious", label: "High or critical", match: (row) => row.worstSeverity === "high" || row.worstSeverity === "critical" },
    { key: "no_next", label: "No session booked", match: (row) => !row.nextSession && (row.openCases > 0 || Boolean(row.activePlan)) },
  ],
  category: { label: "Class", value: (row) => row.class_name },
  sorts: [
    { key: "severity", label: "Most serious first", compare: (a, b) => rank[b.worstSeverity] - rank[a.worstSeverity] || b.openCases - a.openCases },
    { key: "name", label: "Learner A–Z", compare: byText((row) => row.student_name) },
    { key: "last", label: "Seen longest ago", compare: byText((row) => row.lastSeen || "0000") },
  ],
};
